/**
 * Terms of Service, fetched live from the company website (env.termsUrl) and
 * extracted from the rendered page so the console never carries its own copy.
 * The page is first-party, but its HTML is still sanitized before injection.
 */
import { brand } from '@/config/brand';
import { env } from '@/config/env';

export const COMPANY_NAME = brand.legalName;
export const TERMS_URL = env.termsUrl;

interface TermsDocument {
  title: string;
  /** "Latest updates" date as printed on the page, or null if absent. */
  updated: string | null;
  html: string;
}

const REMOVE_SELECTORS = 'script,style,noscript,iframe,object,embed,form,button,input';

function sanitize(root: Element): void {
  root.querySelectorAll(REMOVE_SELECTORS).forEach((el) => el.remove());
  root.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on')) el.removeAttribute(attr.name);
      if (
        (attr.name === 'href' || attr.name === 'src') &&
        /^\s*javascript:/i.test(attr.value)
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });
  root.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href') ?? '';
    // Site-relative links would resolve against the console, not the website.
    if (href.startsWith('/')) a.setAttribute('href', new URL(href, TERMS_URL).href);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noreferrer noopener');
  });
}

/** Fetches the Terms page and returns its cleaned body. Throws on network / parse failure. */
export async function fetchTerms(): Promise<TermsDocument> {
  const res = await fetch(TERMS_URL, { headers: { Accept: 'text/html' } });
  if (!res.ok) throw new Error(`Terms request failed (${res.status})`);
  const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

  const body = doc.querySelector('[data-ui="PrivacyPolicy"] .prose') ?? doc.querySelector('.prose');
  if (!body || !body.textContent?.trim()) throw new Error('Could not locate the Terms content');
  sanitize(body);

  const title = doc.querySelector('h1')?.textContent?.trim() || 'Terms of Service';
  const updated =
    /Latest updates:\s*(\d{1,4}[/.-]\d{1,2}[/.-]\d{1,4})/i.exec(doc.body.textContent ?? '')?.[1] ?? null;

  return { title, updated, html: body.innerHTML };
}
